import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Calendar,
  Search,
  RefreshCw,
  Eye,
  Settings,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import adminService, {
  Settlement,
  SettlementStatus,
  WeeklySettlementSchedule,
  WalletStatistics
} from '../../services/admin/adminService';
import { formatCurrency } from '../../utils/formatters';

type ViewMode = 'all' | 'pending' | 'approved' | 'rejected' | 'weekly-schedules' | 'wallet-stats';

const WalletSettlementManagement: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [weeklySchedules, setWeeklySchedules] = useState<WeeklySettlementSchedule[]>([]);
  const [walletStats, setWalletStats] = useState<WalletStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<SettlementStatus | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSettlements, setTotalSettlements] = useState(0);

  // Modals
  const [showInstantSettlementModal, setShowInstantSettlementModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showWalletAdjustModal, setShowWalletAdjustModal] = useState(false);

  // Approve/Reject Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSettlementForAction, setSelectedSettlementForAction] = useState<Settlement | null>(null);
  const [actionNote, setActionNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Instant Settlement Form
  const [instantBusinessId, setInstantBusinessId] = useState('');
  const [instantAmount, setInstantAmount] = useState('');
  const [instantNotes, setInstantNotes] = useState('');

  // Weekly Schedule Form
  const [editingSchedule, setEditingSchedule] = useState<WeeklySettlementSchedule | null>(null);
  const [scheduleBusinessId, setScheduleBusinessId] = useState('');
  const [scheduleDay, setScheduleDay] = useState<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'>('monday');
  const [scheduleTime, setScheduleTime] = useState('10:00');
  const [scheduleMinAmount, setScheduleMinAmount] = useState('');
  const [scheduleIsActive, setScheduleIsActive] = useState(true);

  // Wallet Adjust Form
  const [adjustBusinessId, setAdjustBusinessId] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit');
  const [adjustReason, setAdjustReason] = useState('');

  useEffect(() => {
    loadData();
  }, [viewMode, currentPage, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (viewMode === 'weekly-schedules') {
        await loadWeeklySchedules();
      } else if (viewMode === 'wallet-stats') {
        await loadWalletStatistics();
      } else {
        await loadSettlements();
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSettlements = async () => {
    const params: any = {
      page: currentPage,
      limit: 20
    };

    if (statusFilter) {
      params.status = statusFilter;
    } else if (viewMode === 'pending') {
      params.status = 'pending';
    } else if (viewMode === 'approved') {
      params.status = 'approved';
    } else if (viewMode === 'rejected') {
      params.status = 'rejected';
    }

    const response = await adminService.getAllSettlements(params);
    setSettlements(response.settlements);
    setTotalPages(response.pagination.total_pages);
    setTotalSettlements(response.pagination.total_settlements);
  };

  const loadWeeklySchedules = async () => {
    const response = await adminService.getWeeklySettlementSchedules({ page: currentPage, limit: 20 });
    setWeeklySchedules(response.schedules);
    setTotalPages(response.pagination.total_pages);
  };

  const loadWalletStatistics = async () => {
    const stats = await adminService.getWalletStatistics();
    setWalletStats(stats);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleApproveSettlement = (settlement: Settlement) => {
    setSelectedSettlementForAction(settlement);
    setActionNote('');
    setShowApproveModal(true);
  };

  const handleRejectSettlement = (settlement: Settlement) => {
    setSelectedSettlementForAction(settlement);
    setActionNote('');
    setShowRejectModal(true);
  };

  const processApprove = async () => {
    if (!selectedSettlementForAction) return;
    setActionLoading(true);
    try {
      await adminService.approveSettlement({
        settlement_id: selectedSettlementForAction.settlement_id,
        notes: actionNote
      });
      toast.success('Settlement approved successfully');
      setShowApproveModal(false);
      loadSettlements();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve settlement');
    } finally {
      setActionLoading(false);
    }
  };

  const processReject = async () => {
    if (!selectedSettlementForAction) return;
    if (!actionNote.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await adminService.rejectSettlement({
        settlement_id: selectedSettlementForAction.settlement_id,
        rejection_reason: actionNote
      });
      toast.success('Settlement rejected successfully');
      setShowRejectModal(false);
      loadSettlements();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject settlement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (settlement: Settlement) => {
    navigate(`/admin/settlements/${settlement.settlement_id}`);
  };

  const handleInstantSettlement = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await adminService.processInstantSettlement({
        business_id: Number(instantBusinessId),
        amount: Number(instantAmount),
        notes: instantNotes
      });
      toast.success('Instant settlement processed successfully');
      setShowInstantSettlementModal(false);
      setInstantBusinessId('');
      setInstantAmount('');
      setInstantNotes('');
      await loadSettlements();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to process instant settlement');
    }
  };

  const handleSaveSchedule = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingSchedule) {
        await adminService.updateWeeklySettlementSchedule({
          schedule_id: editingSchedule.schedule_id,
          settlement_day: scheduleDay,
          settlement_time: scheduleTime,
          is_active: scheduleIsActive,
          minimum_amount: scheduleMinAmount ? Number(scheduleMinAmount) : undefined
        });
        toast.success('Schedule updated successfully');
      } else {
        await adminService.createWeeklySettlementSchedule({
          business_id: scheduleBusinessId ? Number(scheduleBusinessId) : undefined,
          settlement_day: scheduleDay,
          settlement_time: scheduleTime,
          is_active: scheduleIsActive,
          minimum_amount: scheduleMinAmount ? Number(scheduleMinAmount) : undefined
        });
        toast.success('Schedule created successfully');
      }

      setShowScheduleModal(false);
      resetScheduleForm();
      await loadWeeklySchedules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    try {
      await adminService.deleteWeeklySettlementSchedule(scheduleId);
      toast.success('Schedule deleted successfully');
      await loadWeeklySchedules();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete schedule');
    }
  };

  const handleWalletAdjust = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!adjustReason || adjustReason.length < 10) {
      toast.error('Please provide a detailed reason (at least 10 characters)');
      return;
    }

    try {
      await adminService.adjustWalletBalance({
        business_id: Number(adjustBusinessId),
        amount: Number(adjustAmount),
        adjustment_type: adjustType,
        reason: adjustReason
      });
      toast.success('Wallet balance adjusted successfully');
      setShowWalletAdjustModal(false);
      resetWalletAdjustForm();
      await loadWalletStatistics();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to adjust wallet balance');
    }
  };

  const openEditSchedule = (schedule: WeeklySettlementSchedule) => {
    setEditingSchedule(schedule);
    setScheduleBusinessId(schedule.business_id?.toString() || '');
    setScheduleDay(schedule.settlement_day);
    setScheduleTime(schedule.settlement_time.substring(0, 5)); // HH:MM format
    setScheduleIsActive(schedule.is_active === 1);
    setScheduleMinAmount(schedule.minimum_amount);
    setShowScheduleModal(true);
  };

  const resetScheduleForm = () => {
    setEditingSchedule(null);
    setScheduleBusinessId('');
    setScheduleDay('monday');
    setScheduleTime('10:00');
    setScheduleIsActive(true);
    setScheduleMinAmount('');
  };

  const resetWalletAdjustForm = () => {
    setAdjustBusinessId('');
    setAdjustAmount('');
    setAdjustType('credit');
    setAdjustReason('');
  };

  const getStatusBadge = (status: SettlementStatus) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-primary-100 text-primary-800',
      failed: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredSettlements = settlements.filter(settlement =>
    settlement.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    settlement.settlement_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Wallet & Settlement Management</h1>
          <p className="text-secondary-600 mt-1">Manage business payouts, settlements, and wallet balances</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
          {/* <button
            onClick={() => setShowInstantSettlementModal(true)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Zap size={18} />
            Instant Settlement
          </button>
          <button
            onClick={() => setShowWalletAdjustModal(true)}
            className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors flex items-center gap-2"
          >
            <Wallet size={18} />
            Adjust Wallet
          </button> */}
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          {[
            { mode: 'all' as ViewMode, label: 'All Settlements', icon: DollarSign },
            { mode: 'pending' as ViewMode, label: 'Pending', icon: Clock },
            { mode: 'approved' as ViewMode, label: 'Approved', icon: CheckCircle },
            { mode: 'rejected' as ViewMode, label: 'Rejected', icon: XCircle },
          ].map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => {
                setViewMode(mode);
                setCurrentPage(1);
              }}
              className={`flex-1 px-6 py-4 font-semibold transition-colors relative flex items-center justify-center gap-2 ${viewMode === mode
                ? 'text-primary-600 border-b-2 border-primary-500 bg-primary-50'
                : 'text-secondary-600 hover:text-primary-600 hover:bg-gray-50'
                }`}
            >
              <Icon size={20} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Wallet Statistics View */}
      {viewMode === 'wallet-stats' && walletStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">Total Business Wallet Balance</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">
                  {formatCurrency(walletStats.total_business_wallet_balance, 'INR', 'en-IN', '₹')}
                </p>
              </div>
              <div className="p-3 bg-primary-100 rounded-lg">
                <Wallet size={24} className="text-primary-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">Total User Wallet Balance</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">
                  {formatCurrency(walletStats.total_user_wallet_balance, 'INR', 'en-IN', '₹')}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <DollarSign size={24} className="text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">Pending Settlements</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">
                  {formatCurrency(walletStats.pending_settlement_amount, 'INR', 'en-IN', '₹')}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {walletStats.pending_settlement_requests} requests
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock size={24} className="text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">Platform Earnings</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">
                  {formatCurrency(walletStats.platform_earnings, 'INR', 'en-IN', '₹')}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp size={24} className="text-green-600" />
              </div>
            </div>
          </div>


          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary-600">Pending Payouts</p>
                <p className="text-2xl font-bold text-secondary-900 mt-1">
                  {formatCurrency(walletStats.pending_payouts, 'INR', 'en-IN', '₹')}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Download size={24} className="text-orange-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Schedules View */}
      {viewMode === 'weekly-schedules' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-secondary-900">Weekly Settlement Schedules</h2>
            <button
              onClick={() => {
                resetScheduleForm();
                setShowScheduleModal(true);
              }}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              <Calendar size={18} />
              Create Schedule
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Business</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Day</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Min Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {weeklySchedules.map((schedule) => (
                  <tr key={schedule.schedule_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-secondary-900">
                        {schedule.business_name || 'All Businesses'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-secondary-700 capitalize">{schedule.settlement_day}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-secondary-700">{schedule.settlement_time}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-secondary-700 font-semibold">
                        {formatCurrency(schedule.minimum_amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${schedule.is_active === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                        {schedule.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditSchedule(schedule)}
                          className="text-primary-600 hover:text-primary-800"
                        >
                          <Settings size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteSchedule(schedule.schedule_id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Settlements List View */}
      {viewMode !== 'weekly-schedules' && viewMode !== 'wallet-stats' && (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by business name or settlement number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as SettlementStatus | '');
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Settlements Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-secondary-900">
                {viewMode === 'all' ? 'All Settlements' : `${viewMode.charAt(0).toUpperCase() + viewMode.slice(1)} Settlements`}
                <span className="ml-2 text-sm font-normal text-secondary-600">
                  ({totalSettlements} total)
                </span>
              </h2>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <RefreshCw className="animate-spin text-primary-500" size={32} />
              </div>
            ) : filteredSettlements.length === 0 ? (
              <div className="text-center py-12 text-secondary-600">
                <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
                <p>No settlements found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Settlement #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredSettlements.map((settlement) => (
                      <tr key={settlement.settlement_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-secondary-900">{settlement.settlement_number}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-secondary-900">{settlement.business_name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-secondary-900">
                            {formatCurrency(settlement.amount, settlement.currency_code, 'en-IN', settlement.currency_symbol)}
                          </div>
                          <div className="text-xs text-secondary-500">
                            Net: {formatCurrency(settlement.net_amount, settlement.currency_code, 'en-IN', settlement.currency_symbol)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-secondary-700 capitalize">{settlement.settlement_type}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(settlement.status)}`}>
                            {settlement.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">
                          {new Date(settlement.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleViewDetails(settlement)}
                              className="text-primary-600 hover:text-primary-800 flex items-center gap-1"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            {(settlement.status === 'pending' || settlement.status === 'processing') && (
                              <>
                                <button
                                  onClick={() => handleApproveSettlement(settlement)}
                                  className="text-green-600 hover:text-green-800 flex items-center gap-1"
                                  title="Approve Settlement"
                                >
                                  <CheckCircle size={18} />
                                </button>
                                <button
                                  onClick={() => handleRejectSettlement(settlement)}
                                  className="text-danger-600 hover:text-danger-800 flex items-center gap-1"
                                  title="Reject Settlement"
                                >
                                  <XCircle size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-secondary-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Instant Settlement Modal */}
      {showInstantSettlementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900">Process Instant Settlement</h3>
            </div>
            <form onSubmit={handleInstantSettlement} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Business ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={instantBusinessId}
                  onChange={(e) => setInstantBusinessId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter business ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={instantAmount}
                  onChange={(e) => setInstantAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={instantNotes}
                  onChange={(e) => setInstantNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Add notes about this instant settlement..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Process Settlement
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInstantSettlementModal(false);
                    setInstantBusinessId('');
                    setInstantAmount('');
                    setInstantNotes('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-secondary-900 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Weekly Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900">
                {editingSchedule ? 'Edit' : 'Create'} Weekly Schedule
              </h3>
            </div>
            <form onSubmit={handleSaveSchedule} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Business ID (Optional - Leave empty for all businesses)
                </label>
                <input
                  type="number"
                  value={scheduleBusinessId}
                  onChange={(e) => setScheduleBusinessId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter business ID or leave empty"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Settlement Day <span className="text-red-500">*</span>
                </label>
                <select
                  value={scheduleDay}
                  onChange={(e) => setScheduleDay(e.target.value as any)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                  <option value="sunday">Sunday</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Settlement Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Minimum Amount (Optional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={scheduleMinAmount}
                  onChange={(e) => setScheduleMinAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter minimum amount"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="scheduleIsActive"
                  checked={scheduleIsActive}
                  onChange={(e) => setScheduleIsActive(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="scheduleIsActive" className="text-sm font-medium text-secondary-700">
                  Active Schedule
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  {editingSchedule ? 'Update' : 'Create'} Schedule
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowScheduleModal(false);
                    resetScheduleForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-secondary-900 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Wallet Adjust Modal */}
      {showWalletAdjustModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-secondary-900">Adjust Wallet Balance</h3>
            </div>
            <form onSubmit={handleWalletAdjust} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Business ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={adjustBusinessId}
                  onChange={(e) => setAdjustBusinessId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter business ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Adjustment Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={adjustType}
                  onChange={(e) => setAdjustType(e.target.value as 'credit' | 'debit')}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="credit">Credit (Add Funds)</option>
                  <option value="debit">Debit (Deduct Funds)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Reason <span className="text-red-500">*</span> (Min 10 characters)
                </label>
                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  required
                  minLength={10}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Provide a detailed reason for this adjustment..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Adjust Wallet
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowWalletAdjustModal(false);
                    resetWalletAdjustForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-secondary-900 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedSettlementForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center text-secondary-900 mb-2">Approve Settlement</h3>
              <p className="text-center text-secondary-600 mb-6">
                Are you sure you want to approve settlement #{selectedSettlementForAction.settlement_number} for {formatCurrency(selectedSettlementForAction.amount, selectedSettlementForAction.currency_code, 'en-IN', selectedSettlementForAction.currency_symbol)}?
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Approval Notes (Optional)
                </label>
                <textarea
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Add a note..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={processApprove}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  {actionLoading ? <RefreshCw className="animate-spin" size={18} /> : null}
                  Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedSettlementForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="p-6">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={32} className="text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center text-secondary-900 mb-2">Reject Settlement</h3>
              <p className="text-center text-secondary-600 mb-6">
                Rejecting settlement #{selectedSettlementForAction.settlement_number} for {formatCurrency(selectedSettlementForAction.amount, selectedSettlementForAction.currency_code, 'en-IN', selectedSettlementForAction.currency_symbol)}.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Explain why this settlement is being rejected..."
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={processReject}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  {actionLoading ? <RefreshCw className="animate-spin" size={18} /> : null}
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletSettlementManagement;

